import { MessageCircle, Send, ExternalLink, Sparkles, Loader2, RotateCcw, Lock } from "lucide-react";
import { C } from "../../theme/tokens";
import { openExternalLink } from "../../utils/video";

/* One row inside the VIP block once links are loaded: icon chip,
   label + sub, external-link affordance on the right. Matches the
   settings panel's row rhythm rather than becoming its own card. */
function VipLinkRow({ icon: Icon, title, sub, url }) {
  return (
    <button
      onClick={() => openExternalLink(url)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <div
        style={{
          flex: "0 0 auto",
          width: 38,
          height: 38,
          borderRadius: 12,
          background: C.violetDeep,
          border: `1px solid ${C.violet}55`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={17} color={C.violetBright} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 14.5,
            color: C.primary,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 12, color: C.mutedB, marginTop: 2 }}>{sub}</div>
      </div>
      <ExternalLink size={16} color={C.mutedB} style={{ flexShrink: 0 }} />
    </button>
  );
}

/* VIP chat / VIP channel block — always present in the profile.
   Starts in "idle": a short blurb + a "Generate" button, no request
   fired yet. Tapping it calls GET user/vip-links (via the generate()
   action from useVipLinks) and the same block then shows whichever
   of vip_chat/vip_channel came back, each as a tappable row. */
export function VipLinksCard({ t, status, vipChat, vipChannel, onGenerate, trialLocked = false }) {
  const loading = status === "loading";
  const hasAnyLink = Boolean(vipChat || vipChannel);

  return (
    <div
      style={{
        display: "flex",
        background: `linear-gradient(160deg, ${C.violet}14, transparent 70%), ${C.panel1}`,
        border: `1px solid ${C.violet}48`,
        overflow: "visible",
      }}
    >
      {/* Ticket stub — vertical "VIP" label with punched perforation
          dots on its dashed right edge, echoing the reference VIP
          pass rather than the generic dashed-left-border box every
          other card here uses. */}
      <div
        style={{
          position: "relative",
          flex: "0 0 auto",
          width: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px dashed ${C.violet}66`,
        }}
      >
        <div
          style={{
            position: "absolute",
            right: -6,
            top: -6,
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: C.canvas,
            border: `1px solid ${C.violet}48`,
          }}
        />
        <div
          style={{
            position: "absolute",
            right: -6,
            bottom: -6,
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: C.canvas,
            border: `1px solid ${C.violet}48`,
          }}
        />
        <span
          style={{
            writingMode: "vertical-rl",
            fontSize: 11,
            letterSpacing: "0.14em",
            color: C.violetBright,
            fontWeight: 600,
          }}
        >
          VIP
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        {trialLocked ? (
          <div style={{ padding: "16px 16px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div
              style={{
                flex: "0 0 auto",
                width: 38,
                height: 38,
                borderRadius: 12,
                background: C.violetDeep,
                border: `1px solid ${C.violet}55`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Lock size={17} color={C.violetBright} />
            </div>
            <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5, margin: 0 }}>
              {t.prof_vip_trial_locked}
            </p>
          </div>
        ) : status === "ready" && hasAnyLink ? (
          <>
            {vipChat && (
              <VipLinkRow icon={MessageCircle} title={t.prof_vip_chat} sub={t.prof_vip_chat_sub} url={vipChat} />
            )}
            {vipChat && vipChannel && <div style={{ height: 1, background: C.hairline, margin: "0 16px" }} />}
            {vipChannel && (
              <VipLinkRow icon={Send} title={t.prof_vip_channel} sub={t.prof_vip_channel_sub} url={vipChannel} />
            )}
            <div style={{ height: 1, background: C.hairline, margin: "0 16px" }} />
            <button
              onClick={onGenerate}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "12px 16px",
                border: "none",
                background: "transparent",
                color: C.mutedB,
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                fontSize: 12.5,
                cursor: "pointer",
              }}
            >
              <RotateCcw size={13} />
              {t.prof_vip_refresh}
            </button>
          </>
        ) : (
          <div style={{ padding: "16px 16px 18px" }}>
            <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5, margin: "0 0 14px" }}>
              {status === "error" ? t.prof_vip_error : t.prof_vip_desc}
            </p>
            <button
              onClick={onGenerate}
              disabled={loading}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "13px 18px",
                borderRadius: 12,
                border: "none",
                background: loading
                  ? C.mutedA
                  : `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`,
                color: loading ? C.mutedB : "#0b0d13",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: "-0.01em",
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: loading ? "none" : `0 8px 26px -8px ${C.violetBright}99`,
              }}
            >
              {loading ? (
                <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} />
              ) : (
                <Sparkles size={16} />
              )}
              {loading ? t.prof_vip_generating : t.prof_vip_generate}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
